import type { ZodSchema } from 'zod';
import { ValidationError } from './errors';

/**
 * Builds a query string from an object of parameters
 * @param params - Object containing query parameters
 * @returns Query string (without the leading '?')
 */
export const buildQueryString = <T extends object>(params: T): string => {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      if (Array.isArray(value)) {
        value.forEach((item) => {
          if (item !== undefined && item !== null && item !== '') {
            searchParams.append(key, String(item));
          }
        });
      } else {
        searchParams.append(key, String(value));
      }
    }
  });

  return searchParams.toString();
};

/**
 * Builds a complete URL with query parameters
 * @param baseUrl - The base URL
 * @param params - Object containing query parameters
 * @returns Complete URL with query parameters
 */
export const buildUrlWithParams = <T extends object>(baseUrl: string, params: T): string => {
  const queryString = buildQueryString(params);
  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
};

/**
 * Validates API response data using Zod schema with safeParse
 * Throws ValidationError if validation fails
 * 
 * @param schema - Zod schema to validate against
 * @param data - Data to validate
 * @param context - Optional context for better error messages (e.g., 'company data', 'user list')
 * @returns Validated and typed data
 * 
 * @example
 * const users = validateResponse(z.array(userSchema), response.data, 'user list');
 */
export const validateResponse = <T>(
  schema: ZodSchema<T>,
  data: unknown,
  context?: string
): T => {
  const result = schema.safeParse(data);
  
  if (!result.success) {
    const errorMessage = context 
      ? `Failed to validate ${context}` 
      : 'Response validation failed';
    
    if (import.meta.env.DEV) {
      console.error('Validation Error:', {
        context,
        errors: result.error.issues,
        data,
      });
    }
    
    throw new ValidationError(errorMessage, result.error, data);
  }
  
  return result.data;
};

/**
 * Extract nested data from standard API response format
 * Handles common patterns: response.data.response_output.list.content
 * 
 * @param response - Axios response object
 * @param path - Optional custom path (default: 'response_output.list.content')
 * @returns Extracted data or original response.data
 */
export const extractApiData = <T = unknown>(
  response: { data: unknown },
  path?: string
): T => {
  const data = response.data as Record<string, unknown>;

  if (!path) {
    const output = data?.response_output as Record<string, unknown>;
    const list = output?.list as Record<string, unknown>;
    return (list?.content ?? response.data) as T;
  }
  
  const keys = path.split('.');
  let result: unknown = response.data;
  
  for (const key of keys) {
    if (typeof result !== 'object' || result === null || !(key in result)) {
      return response.data as T;
    }
    result = (result as Record<string, unknown>)[key];
  }
  
  return result as T;
};

/**
 * Extracts human readable error message from API response errors array, response message, or AxiosError
 * 
 * @param error - The error object from catch or onError
 * @param defaultMessage - Optional fallback message
 * @returns Extracted error message string
 */
export const getApiErrorMessage = (error: unknown, defaultMessage: string = 'An error occurred'): string => {
  const errObj = error as Record<string, unknown> | null | undefined;
  const responseData = (errObj?.response as Record<string, unknown> | undefined)?.data as
    | Record<string, unknown>
    | undefined;
  const responseOutput = responseData?.response_output as Record<string, unknown> | undefined;
  const errors = responseOutput?.errors || responseData?.errors;

  if (Array.isArray(errors) && errors.length > 0) {
    const errorMessages = errors
      .map((err) => {
        if (typeof err === 'string' && err.trim()) return err.trim();
        if (err && typeof err === 'object') {
          const rec = err as Record<string, unknown>;
          return (typeof rec.message === 'string' ? rec.message : null) || (typeof rec.field === 'string' ? String(rec.field) : null);
        }
        return null;
      })
      .filter(Boolean);

    if (errorMessages.length > 0) {
      return errorMessages.join('. ');
    }
  }

  if (errors && typeof errors === 'object' && !Array.isArray(errors)) {
    const values = Object.values(errors as Record<string, unknown>);
    if (values.length > 0) {
      const messages: string[] = [];
      for (const val of values) {
        if (typeof val === 'string' && val.trim()) {
          messages.push(val.trim());
        } else if (Array.isArray(val)) {
          for (const item of val) {
            if (typeof item === 'string' && item.trim()) {
              messages.push(item.trim());
            } else if (item && typeof item === 'object' && 'message' in item) {
              const msg = (item as Record<string, unknown>).message;
              if (msg !== undefined && msg !== null) {
                messages.push(String(msg).trim());
              }
            }
          }
        } else if (val && typeof val === 'object' && 'message' in val) {
          const msg = (val as Record<string, unknown>).message;
          if (msg !== undefined && msg !== null) {
            messages.push(String(msg).trim());
          }
        }
      }
      if (messages.length > 0) {
        return messages.join('. ');
      }
    }
  }

  // Check string response_output
  if (typeof responseData?.response_output === 'string' && responseData.response_output.trim()) {
    return responseData.response_output.trim();
  }

  // Check responseOutput message or error property
  if (typeof responseOutput?.message === 'string' && responseOutput.message.trim()) {
    return responseOutput.message.trim();
  }
  if (typeof responseOutput?.error === 'string' && responseOutput.error.trim()) {
    return responseOutput.error.trim();
  }

  const listErrors = responseOutput?.list;
  if (Array.isArray(listErrors) && listErrors.length > 0) {
    const firstErr = listErrors[0];
    if (typeof firstErr === 'string' && firstErr.trim()) return firstErr.trim();
    if (firstErr && typeof firstErr === 'object' && 'message' in firstErr) {
      const msg = (firstErr as Record<string, unknown>).message;
      if (msg) return String(msg);
    }
  }

  if (typeof responseOutput?.detail === 'string' && responseOutput.detail.trim()) {
    return responseOutput.detail.trim();
  }
  if (responseOutput?.detail && typeof responseOutput.detail === 'object' && 'message' in responseOutput.detail) {
    const detailMsg = (responseOutput.detail as Record<string, unknown>).message;
    if (typeof detailMsg === 'string' && detailMsg.trim()) return detailMsg.trim();
  }

  // Check top-level message before generic response_schema
  if (typeof responseData?.message === 'string' && responseData.message.trim()) {
    return responseData.message.trim();
  }

  // Check error_description (OAuth/standard)
  if (typeof responseData?.error_description === 'string' && responseData.error_description.trim()) {
    return responseData.error_description.trim();
  }

  // Check detail on root
  if (typeof responseData?.detail === 'string' && responseData.detail.trim()) {
    return responseData.detail.trim();
  }

  if (responseData?.error) {
    if (typeof responseData.error === 'string' && responseData.error.trim()) {
      return responseData.error.trim();
    }
    if (typeof responseData.error === 'object' && 'message' in responseData.error) {
      const errObjMsg = (responseData.error as Record<string, unknown>).message;
      if (typeof errObjMsg === 'string' && errObjMsg.trim()) return errObjMsg.trim();
    }
  }

  const responseSchema = responseData?.response_schema as Record<string, unknown> | undefined;
  if (typeof responseSchema?.response_message === 'string') {
    const schemaMsg = responseSchema.response_message;
    if (
      schemaMsg &&
      schemaMsg.toLowerCase() !== 'error' &&
      !schemaMsg.toLowerCase().startsWith('request failed with status code')
    ) {
      return schemaMsg;
    }
  }

  if (
    typeof errObj?.message === 'string' &&
    errObj.message &&
    !errObj.message.toLowerCase().startsWith('request failed with status code')
  ) {
    return errObj.message;
  }

  return defaultMessage;
};

/**
 * Extracts structured errors array from API response
 */
export const getApiErrorsList = (
  error: unknown,
): Array<{ field?: string; message: string }> => {
  const errObj = error as Record<string, unknown> | null | undefined;
  const responseData = (errObj?.response as Record<string, unknown> | undefined)?.data as
    | Record<string, unknown>
    | undefined;
  const responseOutput = responseData?.response_output as Record<string, unknown> | undefined;
  const errors = responseOutput?.errors || responseData?.errors;

  if (Array.isArray(errors) && errors.length > 0) {
    return errors
      .map((err) => {
        if (typeof err === 'string' && err.trim()) {
          return { message: err.trim() };
        }
        if (err && typeof err === 'object') {
          const rec = err as Record<string, unknown>;
          const message =
            typeof rec.message === 'string' && rec.message.trim()
              ? rec.message.trim()
              : typeof rec.error === 'string' && rec.error.trim()
                ? rec.error.trim()
                : '';
          const field = typeof rec.field === 'string' ? rec.field.trim() : undefined;
          if (message || field) {
            return { field, message: message || (field ? `${field} is invalid` : '') };
          }
        }
        return null;
      })
      .filter((e): e is { field?: string; message: string } => Boolean(e && e.message));
  }

  return [];
};

/**
 * Extracts trace ID from API response or error object
 * 
 * @param error - The error object from catch, onError, or AxiosError
 * @returns Extracted trace ID string or undefined
 */
export const getApiTraceId = (error: unknown): string | undefined => {
  const errObj = error as Record<string, unknown> | null | undefined;
  const responseData = (errObj?.response as Record<string, unknown> | undefined)?.data as
    | Record<string, unknown>
    | undefined;

  if (typeof responseData?.trace_id === 'string' && responseData.trace_id.trim()) {
    return responseData.trace_id.trim();
  }

  if (typeof (error as { traceId?: string })?.traceId === 'string' && (error as { traceId?: string }).traceId?.trim()) {
    return (error as { traceId?: string }).traceId?.trim();
  }

  return undefined;
};
